import {
  all,
  call,
  put,
  take,
  select,
  takeLatest,
  takeEvery
} from 'redux-saga/effects'
import qs from 'query-string'
import { message } from 'antd'
import Http from '@servers/http'
import {
  setUsers,
  resetUser,
  setBrandList,
  setAuthCodes
} from '@redux/actions/basic/user'
import { setSelectedKeys } from '@redux/actions/basic/layout'
import userTypes from '@redux/types/basic/user'
import layoutTypes from '@redux/types/basic/layout'
import { onLogout as layoutOnLogout } from '@saga/basic/layout'
import { onLogout as workbenchOnLogout } from '@saga/workbench'
import { setStore, getStore, removeStore, getItem } from '@utils/storage'
import history from '@utils/history'
import { DEFERRED_SYMBOL } from '@redux/middlewares/deferredActionDispatch'
import { getBrandList } from '@model/channelManagement/brand'
import { getButtonsAuth } from '@model/auth/auth-v2'
import { getCompanyInfo } from '@model/tenant'
import { globalVariable } from '@/config/app'
import g from '@components/GoEasy'
import ITSocket from '@components/ITSocket'
import { setUser } from '@utils/sentry'
import { loginWith2FA } from '@views/Login/api'
import { buildStage1Navigation } from '@components/Layouts/Stage1Navigation'

export function getPostLoginPath({ query, search } = {}, menuData = []) {
  const from = qs.parse(search || '').from || query?.from

  // Preserve an explicit in-app destination from the authentication redirect.
  if (
    typeof from !== 'string' ||
    !from.startsWith('/') ||
    from.startsWith('//') ||
    from.startsWith('/\\')
  ) {
    // Default only to a page that the server menu actually grants.
    return buildStage1Navigation(menuData).landingPath || '/workbench'
  }

  return from
}

function* login(action) {
  const { payload: formVal } = action
  const promise = action[DEFERRED_SYMBOL]
  try {
    const res = yield call(loginWith2FA, formVal, 1, 1)
    message.info(res?.data?.retmsg)
    if (res.code >= 300 || !res.data) {
      promise.reject(res)
      return
    }
    let user = {
      domain: res.data.domain,
      tenantId: res.data.tenantId,
      token: res.data.token,
      name: res.data.employee.name,
      id: res.data.employee.id,
      headPic: res.data.employee.headPic
    }
    yield call(saveUser, user)
    yield call(initSocket, user)
    let r = yield call(getCompanyInfo, {
      tenantId: res.data.tenantId
    })
    if (r.code < 300 && r.data) {
      user.shortName = r.data.shortName
    }
    let s = yield call(Http.AUTH.getPerSettings)
    if (s.code < 300 && s.data) {
      user.perSetting = s.data
    }
    // 设置登录用户信息
    yield call(saveUser, user)
    yield put({
      type: layoutTypes.GET_APPLICATIONS
    })
    const { type, error } = yield take([
      `${layoutTypes.GET_APPLICATIONS}@DONE`,
      `${layoutTypes.GET_APPLICATIONS}@ERROR`
    ])
    // 获取品牌列表
    yield call(fetchBrandList)
    // 获取所有权限code
    yield call(fetchAuthCodes)

    if (type !== `${layoutTypes.GET_APPLICATIONS}@DONE`) {
      promise.reject(error)
      return
    }

    promise.resolve('login success!')

    const { location, menuData } = yield select(({ router, layout }) => ({
      location: router.location,
      menuData: layout.menuData
    }))
    if (location.state?.autoGoBack) {
      // autoGoBack mode
      history.goBack()
    } else {
      history.push(getPostLoginPath(location, menuData))
    }
  } catch (error) {
    promise.reject(error)
  }
}

/**
 * 用户注销
 * 注销后的记忆跳转，存在有两种模式:
 * - redirect 模式会把当前路径记录在 url query 上
 * - autoGoBack 则是依赖路由的回退
 * 默认注销不会记忆路径
 * 401 注销使用 autoGoBack 模式
 *
 * @param {String} mode mode 为空时，不会记忆跳转
 */
function* logout({ payload: { mode } }) {
  yield put(setSelectedKeys(''))
  yield put(resetUser())
  removeStore('users')
  removeStore('brandList')
  yield all([layoutOnLogout(), workbenchOnLogout()])
  let logoutPath = '/login'

  if (mode === 'redirect') {
    const currentPath = yield select(
      ({ router }) => router.location?.pathname + router.location?.search
    )
    logoutPath +=
      '?' +
      qs.stringify({
        from: currentPath
      })
    history.push(logoutPath)
  } else if (mode === 'autoGoBack') {
    history.push(logoutPath, {
      autoGoBack: true
    })
  } else {
    history.push(logoutPath)
  }
}

export function* saveUser(user) {
  let _user = user
  if (_user) {
    setStore('users', _user)
  } else {
    _user = getStore('users')
  }
  setUser(_user)
  if (_user) {
    yield put(setUsers(_user))
  }
}

function* fetchBrandList() {
  const isLogin = yield select(({ user }) => user.isLogin)
  if (!isLogin) return
  const params = {
    pageNum: 1,
    perPage: 5000
  }
  try {
    const { data } = yield call(getBrandList, params)
    const records =
      data?.list?.map?.(i => ({
        ...i,
        brandPicture: i.brandLogoUrl
      })) || []
    yield call(saveBrandList, records)
  } catch (error) {}
}

export function* fetchAuthCodes() {
  const { data, success } = yield call(getButtonsAuth)
  if (success) {
    const records = data || []
    yield call(saveAuthCodes, records)
  }
}

export function* saveBrandList(list) {
  let _list = list
  if (_list) {
    setStore('brandList', _list, 'local')
  } else {
    _list = getStore('brandList', 'local')
  }
  yield put(setBrandList(_list))
}
// NOTE: 保存 权限Code
export function* saveAuthCodes(list) {
  let _list = list
  if (_list) {
    setStore('authCodes', _list, 'local')
  } else {
    _list = getStore('authCodes', 'local')
  }
  yield put(setAuthCodes(_list))
}
export function* initSocket() {
  const isLogin = yield select(({ user }) => user.isLogin)
  if (!isLogin) return

  g.socket = new ITSocket({
    url: globalVariable.socketUrl + '?token=' + getItem('token'),
    onConnected: function() {
      console.log('IT Connected')
    },
    onDisconnected: function() {
      console.log('IT Disconnect')
    },
    onConnectFailed: function() {
      console.log('IT Error')
    }
  })
}

export function* initAuthCodes() {
  const isLogin = yield select(({ user }) => user.isLogin)
  if (!isLogin) {
    yield call(saveAuthCodes)
    return
  }

  try {
    // A restored session must refresh permissions because the local cache may be absent or stale.
    yield call(fetchAuthCodes)
  } catch (error) {
    yield call(saveAuthCodes)
  }
}

// NOTE: 初始化 user 数据
export function* initUser() {
  yield call(saveUser)
  yield call(saveBrandList)
  yield call(initAuthCodes)
  yield call(initSocket)
}

export default function* watcherSaga() {
  yield takeLatest(userTypes.LOGIN, login)
  yield takeLatest(userTypes.LOGOUT, logout)
  yield takeEvery(userTypes.FETCH_BRAND_LIST, fetchBrandList)
}
